import { MouseEvent, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as SettingsOutlinedIcon } from '@elementary/common-ui/assets/settings.svg';
import {
  Checkbox,
  Flex,
  FlexColumn,
  IconButton,
  SvgIcon,
} from '@elementary/common-ui/components';
import {
  INCIDENT_LIST_FILTER_OPTIONS,
  INCIDENT_LIST_GROUP_SUMMARY,
  INCIDENT_LIST_PAGED,
  INCIDENTS_SETTINGS,
  TREE_INCIDENT_STATUS_TOTALS,
} from '@elementary/common-ui/constants/queryKeys';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { Divider, Menu as MuiMenu, Tooltip, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useSnackbar from 'shared/hooks/useSnackbar';
import { fontSize, fontWeight, space } from '@elementary/common-ui/style/utils';
import * as api from '../api';
import { GROUPING_STRATEGY_DISPLAY } from '../constants';
import type { IncidentsSettings } from '../types';

const DEFAULT_INCIDENTS_SETTINGS: IncidentsSettings = {
  enable_lineage_grouping: true,
  enable_freshness_grouping: true,
  enable_similar_errors_grouping: true,
  enable_asset_failure_freshness_grouping: true,
};

const MenuContent = styled(FlexColumn)`
  width: 360px;
`;

const Header = styled(FlexColumn)`
  gap: ${space('xs')};
  padding: ${space('lg')};
`;

const RuleRowWrapper = styled(Flex)`
  gap: ${space('md')};
  padding: ${space('lg')};
`;

const RuleRow = ({
  checked,
  onChange,
  disabled,
  disabledTooltip,
  title,
  description,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled: boolean;
  disabledTooltip?: string;
  title: string;
  description: string;
}) => (
  <RuleRowWrapper>
    <Tooltip title={disabledTooltip}>
      <Flex sx={{ pt: 0.25 }}>
        <Checkbox
          $noPadding
          checked={checked}
          inputProps={{ 'aria-label': title }}
          onChange={(_, checked) => onChange(checked)}
          disabled={disabled}
        />
      </Flex>
    </Tooltip>

    <FlexColumn sx={{ gap: 0.25 }}>
      <Typography
        sx={{ fontSize: fontSize('base'), fontWeight: fontWeight('medium') }}
      >
        {title}
      </Typography>
      <Typography sx={{ color: 'content.tertiary', fontSize: fontSize('sm') }}>
        {description}
      </Typography>
    </FlexColumn>
  </RuleRowWrapper>
);

const IncidentSettingsMenu = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selectedEnvId = useSelectedEnv();
  const hasPermission = useEnvPermission();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const canEdit = hasPermission('EDIT_INCIDENTS');
  const queryKey = [INCIDENTS_SETTINGS, selectedEnvId];

  const { data: settings, isLoading } = useQuery(queryKey, () =>
    api.fetchIncidentsSettings(selectedEnvId!),
  );

  const invalidateIncidentQueries = () => {
    queryClient.invalidateQueries(queryKey);
    queryClient.invalidateQueries([INCIDENT_LIST_PAGED], { exact: false });
    queryClient.invalidateQueries([INCIDENT_LIST_GROUP_SUMMARY], {
      exact: false,
    });
    queryClient.invalidateQueries([INCIDENT_LIST_FILTER_OPTIONS], {
      exact: false,
    });
    queryClient.invalidateQueries([TREE_INCIDENT_STATUS_TOTALS], {
      exact: false,
    });
  };

  const { mutateAsync: updateSettings, isLoading: isUpdating } = useMutation(
    (nextSettings: NonNullable<typeof settings>) =>
      api.updateIncidentsSettings(selectedEnvId!, nextSettings),
    {
      onSuccess: invalidateIncidentQueries,
    },
  );

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSettingsChange = async (nextSettings: IncidentsSettings) => {
    try {
      await updateSettings(nextSettings);
      openSnackbar({
        variant: 'success',
        message: 'Incident settings updated',
      });
    } catch {
      openSnackbar({
        variant: 'error',
        message: 'Incident settings update failed',
      });
    }
  };

  const handleFreshnessGroupingChange = async (enabled: boolean) => {
    await handleSettingsChange({
      ...(settings ?? DEFAULT_INCIDENTS_SETTINGS),
      enable_freshness_grouping: enabled,
    });
  };

  const handleLineageGroupingChange = async (enabled: boolean) => {
    await handleSettingsChange({
      ...(settings ?? DEFAULT_INCIDENTS_SETTINGS),
      enable_lineage_grouping: enabled,
    });
  };

  const handleSimilarErrorsGroupingChange = async (enabled: boolean) => {
    await handleSettingsChange({
      ...(settings ?? DEFAULT_INCIDENTS_SETTINGS),
      enable_similar_errors_grouping: enabled,
    });
  };

  const handleAssetFailureFreshnessGroupingChange = async (
    enabled: boolean,
  ) => {
    await handleSettingsChange({
      ...(settings ?? DEFAULT_INCIDENTS_SETTINGS),
      enable_asset_failure_freshness_grouping: enabled,
    });
  };

  const disabledTooltip = !canEdit
    ? 'No permissions to modify incident settings'
    : '';

  return (
    <>
      <Tooltip title="Incident settings">
        <IconButton aria-label="Incident settings" onClick={handleOpen}>
          <SvgIcon width={20}>
            <SettingsOutlinedIcon />
          </SvgIcon>
        </IconButton>
      </Tooltip>

      <MuiMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ sx: { p: 0 } }}
      >
        <MenuContent>
          <Header>
            <Typography
              sx={{
                fontSize: fontSize('base'),
                fontWeight: fontWeight('semibold'),
              }}
            >
              Auto-merge rules
            </Typography>
            <Typography
              sx={{ color: 'content.tertiary', fontSize: fontSize('sm') }}
            >
              Automatically group new incidents with existing open ones when
              they match.
            </Typography>
          </Header>

          <Divider />

          <RuleRow
            title={GROUPING_STRATEGY_DISPLAY.asset_failure_freshness}
            description="Auto-merge asset runs that fail with the same error, and add related freshness issues when they open."
            checked={settings?.enable_asset_failure_freshness_grouping ?? true}
            onChange={(checked) =>
              handleAssetFailureFreshnessGroupingChange(checked)
            }
            disabled={!canEdit || isLoading || isUpdating}
            disabledTooltip={disabledTooltip}
          />

          <Divider />

          <RuleRow
            title={GROUPING_STRATEGY_DISPLAY.lineage}
            description="Auto-merge tests of the same type on upstream or downstream assets and columns."
            checked={settings?.enable_lineage_grouping ?? true}
            onChange={(checked) => handleLineageGroupingChange(checked)}
            disabled={!canEdit || isLoading || isUpdating}
            disabledTooltip={disabledTooltip}
          />

          <Divider />

          <RuleRow
            title={GROUPING_STRATEGY_DISPLAY.similar_errors}
            description="Auto-merge failed tests with the same error, such as a missing table, or with similar error messages."
            checked={settings?.enable_similar_errors_grouping ?? true}
            onChange={(checked) => handleSimilarErrorsGroupingChange(checked)}
            disabled={!canEdit || isLoading || isUpdating}
            disabledTooltip={disabledTooltip}
          />

          <Divider />

          <RuleRow
            title={GROUPING_STRATEGY_DISPLAY.freshness}
            description="Auto-merge freshness incidents that affect the same source as an existing open incident."
            checked={settings?.enable_freshness_grouping ?? true}
            onChange={(checked) => handleFreshnessGroupingChange(checked)}
            disabled={!canEdit || isLoading || isUpdating}
            disabledTooltip={disabledTooltip}
          />
        </MenuContent>
      </MuiMenu>
    </>
  );
};

export default IncidentSettingsMenu;
